import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const GenerationContext = createContext({
    // Generation state
    activeGeneration: null,
    generationHistory: [],
    
    // Generation actions
    startGeneration: (template, config, contexts) => void 0,
    updateGenerationProgress: (posts, isLoading) => void 0,
    completeGeneration: (posts) => void 0,
    cancelGeneration: () => void 0,
    clearGeneration: () => void 0,
    
    // Navigation
    navigateToActiveGeneration: () => void 0,
    
    // Utility
    hasActiveGeneration: () => false,
    getGenerationProgress: () => ({ current: 0, total: 0, percentage: 0 })
});

const GenerationProvider = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    
    const [activeGeneration, setActiveGeneration] = useState(null);
    const [generationHistory, setGenerationHistory] = useState([]);
    
    const startGeneration = useCallback((template, config, contexts = []) => {
        const generation = {
            id: `gen_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            template,
            config: { ...config },
            contexts,
            posts: [],
            isLoading: true,
            startedAt: Date.now(),
            estimatedTotal: contexts.length || 1,
            progress: {
                current: 0,
                total: contexts.length || 1,
                percentage: 0
            }
        };
        
        setActiveGeneration(generation);
        return generation.id;
    }, []);
    
    const updateGenerationProgress = useCallback((posts = [], isLoading = true) => {
        setActiveGeneration(prev => {
            if (!prev) return null;
            
            const current = posts.length;
            const total = prev.estimatedTotal;
            const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
            
            return {
                ...prev,
                posts,
                isLoading,
                progress: {
                    current,
                    total,
                    percentage
                },
                updatedAt: Date.now()
            };
        });
    }, []);
    
    const completeGeneration = useCallback((posts = []) => {
        setActiveGeneration(prev => {
            if (!prev) return null;
            
            const completedGeneration = {
                ...prev,
                posts,
                isLoading: false,
                completedAt: Date.now(),
                progress: {
                    current: posts.length,
                    total: prev.estimatedTotal,
                    percentage: 100
                }
            };
            
            // Add to history
            setGenerationHistory(history => {
                const newHistory = [completedGeneration, ...history.filter(h => h.id !== prev.id)];
                return newHistory.slice(0, 10); // Keep only last 10
            });
            
            return completedGeneration;
        });
    }, []);
    
    const cancelGeneration = useCallback(() => {
        setActiveGeneration(prev => {
            if (!prev) return null;
            
            const cancelledGeneration = {
                ...prev,
                isLoading: false,
                cancelled: true,
                cancelledAt: Date.now()
            };
            
            // Add to history
            setGenerationHistory(history => {
                const newHistory = [cancelledGeneration, ...history.filter(h => h.id !== prev.id)];
                return newHistory.slice(0, 10);
            });
            
            return null; // Clear active generation when cancelled
        });
    }, []);
    
    const clearGeneration = useCallback(() => {
        setActiveGeneration(null);
    }, []);
    
    const dismissOnPostSave = useCallback((savedPostId, savedPostGroupId) => {
        // Check if there's an active generation and posts have been saved from it
        if (activeGeneration && !activeGeneration.isLoading) {
            // For completed generations, any save action should dismiss the indicator
            // This ensures the indicator disappears when users save generated posts
            setActiveGeneration(null);
        }
    }, [activeGeneration]);
    
    const navigateToActiveGeneration = useCallback(() => {
        if (activeGeneration?.template) {
            const targetPath = `/post-generator/${activeGeneration.template}`;
            if (location.pathname !== targetPath) {
                navigate(targetPath);
            }
        }
    }, [activeGeneration, navigate, location.pathname]);
    
    const hasActiveGeneration = useCallback(() => {
        return !!(activeGeneration?.isLoading);
    }, [activeGeneration]);
    
    const getGenerationProgress = useCallback(() => {
        if (!activeGeneration) {
            return { current: 0, total: 0, percentage: 0 };
        }
        return activeGeneration.progress;
    }, [activeGeneration]);
    
    // Auto-clear completed generations after 5 minutes
    useEffect(() => {
        if (activeGeneration && !activeGeneration.isLoading && !activeGeneration.cancelled) {
            const timer = setTimeout(() => {
                setActiveGeneration(null);
            }, 5 * 60 * 1000); // 5 minutes
            
            return () => clearTimeout(timer);
        }
    }, [activeGeneration]);
    
    // Listen for post save events
    useEffect(() => {
        const handlePostSaved = (event) => {
            if (event.detail?.postId || event.detail?.postGroupId) {
                dismissOnPostSave(event.detail.postId, event.detail.postGroupId);
            }
        };
        
        window.addEventListener('postSaved', handlePostSaved);
        return () => window.removeEventListener('postSaved', handlePostSaved);
    }, [dismissOnPostSave]);
    
    const contextValue = {
        // State
        activeGeneration,
        generationHistory,
        
        // Actions
        startGeneration,
        updateGenerationProgress,
        completeGeneration,
        cancelGeneration,
        clearGeneration,
        
        // Navigation
        navigateToActiveGeneration,
        
        // Utility
        hasActiveGeneration,
        getGenerationProgress,
        dismissOnPostSave
    };
    
    return (
        <GenerationContext.Provider value={contextValue}>
            {children}
        </GenerationContext.Provider>
    );
};

// Custom hook for using the generation context
const useGeneration = () => {
    const context = useContext(GenerationContext);
    if (!context) {
        throw new Error('useGeneration must be used within a GenerationProvider');
    }
    return context;
};

export { GenerationContext, GenerationProvider, useGeneration };
